import React, { useState, useEffect } from 'react';
import { getMyStats } from '../services/api';
import { BADGE_CATEGORIES, isEarned, getEarnedCount } from '../utils/badgeDefinitions';
import BadgeDetailModal, { BadgeMedallion, getBadgeProgress } from '../components/BadgeDetailModal';

function Badges() {
  const [stats, setStats] = useState({ totalPicks: 0, badges: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBadge, setSelectedBadge] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getMyStats();
      setStats(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Couldn\'t load badges. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const totalEarned = BADGE_CATEGORIES.reduce((sum, cat) => {
    return sum + cat.badges.filter(b => {
      if (b.multi) return getEarnedCount(stats.badges, b.type) > 0;
      return isEarned(stats.badges, b.type);
    }).length;
  }, 0);
  const totalPossible = BADGE_CATEGORIES.reduce((sum, cat) => sum + cat.badges.length, 0);

  const unlockedPct = totalPossible ? Math.round((totalEarned / totalPossible) * 100) : 0;

  return (
    <div className="container bdg-page">
      <header className="bdg-hero pnp-rise">
        <div className="pnp-claw pnp-claw-gold bdg-hero-claw" aria-hidden="true" />
        <div className="bdg-hero-text">
          <p className="pnp-eyebrow pnp-eyebrow-gold">Fighter Card</p>
          <h2 className="pnp-title bdg-hero-title">Badges</h2>
          <p className="bdg-hero-count">
            {loading ? 'Loading...' : `${totalEarned} of ${totalPossible} unlocked`}
          </p>
          {!loading && !error && (
            <div className="bdg-hero-bar" aria-hidden="true">
              <div className="bdg-hero-bar-fill" style={{ width: `${unlockedPct}%` }} />
            </div>
          )}
        </div>
        <div className="bdg-hero-medal" aria-hidden="true">
          <BadgeMedallion
            badge={BADGE_CATEGORIES[0].badges[0]}
            earned={!loading && isEarned(stats.badges, BADGE_CATEGORIES[0].badges[0].type)}
            showLock={false}
            size={112}
          />
        </div>
      </header>

      {loading ? (
        <div className="bdg-cats">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bdg-cat bdg-cat-wide">
              <div className="pnp-skel" style={{ height: 22, width: 160, marginBottom: 18 }} />
              <div className="bdg-grid">
                {[0, 1, 2, 3].map((j) => <div key={j} className="pnp-skel bdg-skel-tile" />)}
              </div>
              <span className="sr-only">Loading badges...</span>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="pnp-empty bdg-error">
          <p className="bdg-error-text">{error}</p>
          <div className="pnp-empty-actions">
            <button onClick={fetchStats} className="btn btn-primary">
              Try again
            </button>
          </div>
        </div>
      ) : (
        <div className="bdg-cats">
          {BADGE_CATEGORIES.map((category, ci) => {
            const earnedInCat = category.badges.filter(b => isEarned(stats.badges, b.type)).length;
            const complete = earnedInCat === category.badges.length;
            return (
              <section
                key={category.key}
                className={`bdg-cat pnp-rise ${category.badges.length <= 2 ? 'bdg-cat-compact' : 'bdg-cat-wide'}`}
                style={{ '--i': ci + 1 }}
              >
                <div className="bdg-cat-head">
                  <h3 className="pnp-title bdg-cat-title">{category.label}</h3>
                  <span className={`pnp-chip ${complete ? 'pnp-chip-green' : 'pnp-chip-mute'} bdg-cat-count`}>
                    {earnedInCat} of {category.badges.length}
                  </span>
                </div>
                <div className="bdg-grid">
                  {category.badges.map(badge => {
                    const earned = isEarned(stats.badges, badge.type);
                    const count = badge.multi ? getEarnedCount(stats.badges, badge.type) : 0;
                    const tooltipText = badge.multi && count > 1
                      ? `${badge.label} x${count}`
                      : badge.label;
                    const progress = earned ? null : getBadgeProgress(badge, stats);

                    return (
                      <div
                        key={badge.type}
                        role="button"
                        tabIndex={0}
                        title={tooltipText}
                        onClick={() => setSelectedBadge({ ...badge, _earned: earned, _count: count })}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedBadge({ ...badge, _earned: earned, _count: count }); } }}
                        className={`bdg-tile ${earned ? 'bdg-tile-earned' : 'bdg-tile-locked'}`}
                      >
                        <BadgeMedallion badge={badge} earned={earned} count={badge.multi ? count : 0} size={76} />
                        <span className="bdg-tile-name">{badge.label}</span>
                        {earned ? (
                          <span className="bdg-tile-sub bdg-tile-sub-earned">
                            {badge.multi && count > 1 ? `Earned ×${count}` : 'Earned'}
                          </span>
                        ) : progress ? (
                          <span className="bdg-progress bdg-tile-progress">
                            <span className="bdg-progress-track"><span className="bdg-progress-fill" style={{ width: `${progress.pct}%`, display: 'block' }} /></span>
                            <span className="bdg-progress-num">{progress.current}/{progress.target.toLocaleString()}</span>
                          </span>
                        ) : (
                          <span className="bdg-tile-sub">Locked</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
      {selectedBadge && (
        <BadgeDetailModal
          badge={selectedBadge}
          earned={selectedBadge._earned}
          count={selectedBadge._count}
          progress={selectedBadge._earned ? null : getBadgeProgress(selectedBadge, stats)}
          onClose={() => setSelectedBadge(null)}
        />
      )}
    </div>
  );
}

export default Badges;
